import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, RotateCw } from "lucide-react";

/**
 * PillarFlipCard — Responsibility / Innovation / Trust flip cards.
 *
 * Behaviour
 *   • Desktop (pointer:fine, hover-capable) — CSS-driven hover preview, plus
 *     click pins the state via `flipped`.
 *   • Mobile / touch — tap toggles `flipped`. An explicit "Back" button on
 *     the reverse face returns to the front.
 *   • Keyboard — Enter/Space toggles, Escape returns to front.
 *   • Respects `prefers-reduced-motion` (cross-fade fallback in CSS).
 *
 * Accessibility
 *   • role="button", tabIndex=0, aria-pressed reflects flipped state.
 *   • Alt text supplied per image via the pillar data record.
 *   • Visible :focus-visible ring, 44 px+ touch targets for the CTA + Back.
 *
 * Scope
 *   • Component is used only in the homepage Story Pillars section.
 *   • Consumes the six approved images from /app/frontend/public/pillars/.
 */
export default function PillarFlipCard({ pillar, index = 0 }) {
  const [flipped, setFlipped] = useState(false);
  const [, setReduceMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduceMotion(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  const toggle = () => setFlipped((f) => !f);
  const onKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggle();
    } else if (e.key === "Escape" && flipped) {
      setFlipped(false);
    }
  };
  const stop = (e) => e.stopPropagation();

  return (
    <div
      className="pillar-flip-card"
      data-flipped={flipped ? "true" : "false"}
      data-pillar-id={pillar.id}
      data-testid={`pillar-${pillar.id}`}
      role="button"
      tabIndex={0}
      aria-pressed={flipped}
      aria-label={`${pillar.eyebrow} — ${pillar.title}. Activate for details.`}
      onClick={toggle}
      onKeyDown={onKey}
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <div className="pillar-flip-inner">
        {/* ---------- FRONT ---------- */}
        <article className="pillar-face pillar-face--front" aria-hidden={flipped}>
          <div className="pillar-image-wrap">
            <img
              src={pillar.front_image}
              alt={pillar.front_alt}
              loading="lazy"
              decoding="async"
              className="pillar-image"
              style={{ objectPosition: pillar.front_position || "center center" }}
              draggable={false}
            />
            <div className="pillar-image-shade" />
          </div>
          <div className="pillar-front-body">
            <div className="pillar-eyebrow">{pillar.eyebrow}</div>
            <h3 className="pillar-title">{pillar.title}</h3>
            {pillar.front_lede && (
              <p className="pillar-body">{pillar.front_lede}</p>
            )}
            <div className="pillar-flip-hint" aria-hidden="true">
              <RotateCw className="h-3.5 w-3.5" />
              <span>Hover or tap</span>
            </div>
          </div>
        </article>

        {/* ---------- BACK ---------- */}
        <article className="pillar-face pillar-face--back" aria-hidden={!flipped}>
          <div className="pillar-image-wrap">
            <img
              src={pillar.back_image}
              alt={pillar.back_alt}
              loading="lazy"
              decoding="async"
              className="pillar-image"
              style={{ objectPosition: pillar.back_position || "center center" }}
              draggable={false}
            />
            <div className="pillar-image-shade" />
          </div>
          <div className="pillar-back-body">
            <div className="pillar-eyebrow">{pillar.eyebrow}</div>
            <h3 className="pillar-title">{pillar.back_heading}</h3>
            {pillar.back_bullets && (
              <ul className="pillar-chips">
                {pillar.back_bullets.map((b) => (
                  <li key={b} className="pillar-chip">
                    {b}
                  </li>
                ))}
              </ul>
            )}
            <div className="pillar-actions">
              <Link
                to={pillar.cta_to || "/about"}
                onClick={stop}
                data-testid={`pillar-cta-${pillar.id}`}
                className="pillar-cta"
              >
                {pillar.cta_label}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <button
                type="button"
                onClick={(e) => {
                  stop(e);
                  setFlipped(false);
                }}
                data-testid={`pillar-back-${pillar.id}`}
                className="pillar-back-btn"
                aria-label="Return to front of card"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back
              </button>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}
